import React, { useEffect, useState } from "react";
import AdminWrapper from "../../../components/AdminWrapper";
import { useDispatch, useSelector } from "react-redux";
import { GetAdmin } from "../../../redux/reducer/GetAdminDetail";
import {
  GetWalletBalance,
  GetWalletTransactions,
  GetRevenueSummary,
  GetRevenueBreakdown,
} from "../../../redux/reducer/OTSWallet";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  TablePagination,
} from "@mui/material";
import {
  AttachMoney,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Receipt,
  CalendarMonth,
  Download,
  Refresh,
  Business,
  Person,
} from "@mui/icons-material";
import { formatCurrency, getCommissionRates, updateCommissionRates } from "../../../utils/commissionCalculator";
import { GetSystemSettings } from "../../../redux/reducer/SystemSettings";
import moment from "moment";
import EarningsReport from "./EarningsReport";

const StatCard = ({ title, value, icon, color, subtitle, trend, trendValue }) => (
  <Card sx={{ borderRadius: 3, border: `1px solid ${color}30`, height: "100%" }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend && (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1, gap: 0.5 }}>
              {trend === "up" ? (
                <TrendingUp sx={{ color: "#22c55e", fontSize: 18 }} />
              ) : (
                <TrendingDown sx={{ color: "#ef4444", fontSize: 18 }} />
              )}
              <Typography variant="caption" sx={{ color: trend === "up" ? "#22c55e" : "#ef4444" }}>
                {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}15`, width: 56, height: 56 }}>
          {React.cloneElement(icon, { sx: { color: color, fontSize: 28 } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const RevenueManagement = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.sidebar.open);
  const { loading: adminLoading, GetAdminDetail } = useSelector((state) => state.getAdminDetail);
  const {
    loading: walletLoading,
    balance,
    transactions,
    transactionsPagination,
    revenueSummary,
  } = useSelector((state) => state.otsWallet);
  const { settings: commissionSettings } = useSelector((state) => state.systemSettings);
  const stats = GetAdminDetail;

  // Update commission rates when settings change
  useEffect(() => {
    if (commissionSettings) {
      updateCommissionRates(commissionSettings);
    }
  }, [commissionSettings]);

  // Get current rates for display
  const currentRates = getCommissionRates();

  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionType, setTransactionType] = useState("all");

  const loading = adminLoading || walletLoading;

  useEffect(() => {
    dispatch(GetAdmin());
    dispatch(GetWalletBalance());
    dispatch(GetRevenueSummary());
    dispatch(GetRevenueBreakdown({ month: moment().month() + 1, year: moment().year() }));
    dispatch(GetSystemSettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(GetWalletTransactions({
      page: transactionPage + 1,
      limit: 10,
      type: transactionType === "all" ? undefined : transactionType
    }));
  }, [dispatch, transactionPage, transactionType]);

  // Calculate revenue stats
  const totalProperties = stats?.property_count || 0;
  const totalLandlords = stats?.landlord_count || 0;
  const totalTenants = stats?.tenant_count || 0;

  // Use transactions from wallet
  const recentTransactions = transactions || [];

  // Handle refresh
  const handleRefresh = () => {
    dispatch(GetAdmin());
    dispatch(GetWalletBalance());
    dispatch(GetRevenueSummary());
    dispatch(GetWalletTransactions({ page: 1, limit: 10 }));
  };

  // Get transaction type label
  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case "advance_commission":
        return "Advance Fee";
      case "rent_commission":
        return "Rent Fee";
      case "late_fee":
        return "Late Fee";
      default:
        return type;
    }
  };

  // Get transaction type color
  const getTransactionTypeColor = (type) => {
    switch (type) {
      case "advance_commission":
        return { bg: "#dbeafe", color: "#1e40af" };
      case "rent_commission":
        return { bg: "#dcfce7", color: "#166534" };
      case "late_fee":
        return { bg: "#fee2e2", color: "#991b1b" };
      default:
        return { bg: "#f3f4f6", color: "#374151" };
    }
  };

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={220} height={40} />
          <Skeleton variant="text" width={200} height={20} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton variant="rounded" width={120} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
          <Skeleton variant="rounded" width={120} height={36} />
        </Box>
      </Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} lg={3} key={i}>
            <Card sx={{ borderRadius: 3, p: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                  <Skeleton variant="text" width={100} height={20} />
                  <Skeleton variant="text" width={80} height={40} />
                  <Skeleton variant="text" width={100} height={16} />
                </Box>
                <Skeleton variant="circular" width={56} height={56} />
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Skeleton variant="text" width={180} height={30} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Skeleton variant="circular" width={60} height={60} sx={{ mx: "auto", mb: 1 }} />
                <Skeleton variant="text" width={60} height={40} sx={{ mx: "auto" }} />
                <Skeleton variant="text" width={100} height={20} sx={{ mx: "auto" }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>
      <Skeleton variant="rounded" width="100%" height={150} sx={{ mb: 4, borderRadius: 3 }} />
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb" }}>
          <Skeleton variant="text" width={180} height={30} />
        </Box>
        <Skeleton variant="rounded" width="100%" height={300} />
      </Paper>
    </>
  );

  return (
    <>
      <AdminWrapper />
      <Box
        sx={{
          ml: { xs: 0, md: open ? "260px" : "65px" },
          transition: "margin 0.3s ease",
          minHeight: "100vh",
          bgcolor: "#f8fafc",
          p: { xs: 2, md: 3 },
          pt: { xs: 10, md: 11 },
        }}
      >
        {loading ? <LoadingSkeleton /> : (
        <>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#1e293b" }}>
              Revenue & Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track platform earnings and financial metrics
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Period"
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="quarter">This Quarter</MenuItem>
                <MenuItem value="year">This Year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ borderColor: "#e5e7eb", color: "#64748b" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              sx={{ bgcolor: "#e94560", "&:hover": { bgcolor: "#d13652" } }}
            >
              Export Report
            </Button>
          </Box>
        </Box>

        {/* Every source OTS earns from, with today / month / range */}
        <EarningsReport />

        {/* Revenue Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Total Revenue"
              value={formatCurrency(revenueSummary?.totalRevenue || balance?.total || 0)}
              subtitle="All time earnings"
              icon={<AttachMoney />}
              color="#22c55e"
              trend={revenueSummary?.growth > 0 ? "up" : revenueSummary?.growth < 0 ? "down" : null}
              trendValue={revenueSummary?.growth ? `${revenueSummary.growth > 0 ? '+' : ''}${revenueSummary.growth}% this month` : null}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Monthly Revenue"
              value={formatCurrency(revenueSummary?.thisMonth || 0)}
              subtitle="Current month"
              icon={<CalendarMonth />}
              color="#6366f1"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Pending Payments"
              value={formatCurrency(balance?.pending || 0)}
              subtitle="Awaiting collection"
              icon={<Receipt />}
              color="#f59e0b"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <StatCard
              title="Available Balance"
              value={formatCurrency(balance?.available || 0)}
              subtitle="Ready for withdrawal"
              icon={<AccountBalance />}
              color="#e94560"
            />
          </Grid>
        </Grid>

        {/* Revenue Breakdown */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Revenue Breakdown
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#dbeafe", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Advance Commission ({currentRates.ADVANCE * 100}%)
                </Typography>
                <Typography variant="h5" fontWeight={700} color="#1e40af">
                  {formatCurrency(revenueSummary?.advanceCommission || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#dcfce7", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Rent Commission ({currentRates.MONTHLY_RENT * 100}%)
                </Typography>
                <Typography variant="h5" fontWeight={700} color="#166534">
                  {formatCurrency(revenueSummary?.rentCommission || 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: "#fee2e2", borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Late Fees (Rs {currentRates.LATE_FEE})
                </Typography>
                <Typography variant="h5" fontWeight={700} color="#991b1b">
                  {formatCurrency(revenueSummary?.lateFees || 0)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Platform Stats */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Platform Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Avatar sx={{ width: 60, height: 60, mx: "auto", mb: 1, bgcolor: "#e9456015" }}>
                  <Business sx={{ color: "#e94560", fontSize: 30 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#e94560" }}>
                  {totalProperties}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Properties
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Avatar sx={{ width: 60, height: 60, mx: "auto", mb: 1, bgcolor: "#22c55e15" }}>
                  <Person sx={{ color: "#22c55e", fontSize: 30 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#22c55e" }}>
                  {totalLandlords}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Landlords
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Avatar sx={{ width: 60, height: 60, mx: "auto", mb: 1, bgcolor: "#f59e0b15" }}>
                  <Person sx={{ color: "#f59e0b", fontSize: 30 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#f59e0b" }}>
                  {totalTenants}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Tenants
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Avatar sx={{ width: 60, height: 60, mx: "auto", mb: 1, bgcolor: "#6366f115" }}>
                  <Receipt sx={{ color: "#6366f1", fontSize: 30 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#6366f1" }}>
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Agreements
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Transaction Ledger */}
        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Transaction Ledger
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={transactionType}
                label="Type"
                onChange={(e) => {
                  setTransactionType(e.target.value);
                  setTransactionPage(0);
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="advance_commission">Advance Fees</MenuItem>
                <MenuItem value="rent_commission">Rent Fees</MenuItem>
                <MenuItem value="late_fee">Late Fees</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Transaction ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Landlord</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentTransactions.length > 0 ? (
                  recentTransactions.map((transaction, index) => {
                    const typeColor = getTransactionTypeColor(transaction.type);
                    return (
                      <TableRow key={transaction._id || index} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {transaction._id?.slice(-8) || `TXN-${index + 1}`}
                          </Typography>
                        </TableCell>
                        <TableCell>{transaction.tenantName || "N/A"}</TableCell>
                        <TableCell>{transaction.landlordName || "N/A"}</TableCell>
                        <TableCell>{transaction.flatName || "N/A"}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "#22c55e" }}>
                          {formatCurrency(transaction.amount || 0)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={getTransactionTypeLabel(transaction.type)}
                            sx={{
                              bgcolor: typeColor.bg,
                              color: typeColor.color,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {transaction.createdAt
                            ? moment(transaction.createdAt).format("DD MMM YYYY, HH:mm")
                            : "N/A"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Receipt sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                      <Typography color="text.secondary">
                        No transactions yet. Revenue will appear here once payments are processed.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {transactionsPagination?.total > 10 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={transactionsPagination?.total || 0}
              rowsPerPage={10}
              page={transactionPage}
              onPageChange={(e, newPage) => setTransactionPage(newPage)}
            />
          )}
        </Paper>
        </>
        )}
      </Box>
    </>
  );
};

export default RevenueManagement;
